import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  User, Star, Trash2, Copy, Mail, Crown, Cloud, LogIn, LogOut, Phone, Check,
  Share2, Users, TrendingUp, Gift, QrCode, Lock, Eye, EyeOff, KeyRound, HelpCircle, ChevronRight,
  Settings as SettingsIcon, Sparkles, Wallet, Receipt, RefreshCw, Image as ImageIcon, CloudOff, Bell, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';
import { useFavoritesStore } from '../stores/favorites.js';
import LoginModal from '../components/LoginModal.jsx';
import ReferralCard from '../components/ReferralCard.jsx';
import PaymentModal from '../components/PaymentModal.jsx';
import ChangePasswordCard from '../components/ChangePasswordCard.jsx';
import { PosterModal } from '../components/PosterModal.jsx';

const TIERS = [
  { id: 'pro_month', name: 'Pro 月付', price: 169, period: '/月', badge: '月付', color: 'text-brand-cyan',
    features: ['全部工具', '缩水器 + 复式 + 组号器', '全量历史(8000+ 期)', '无广告', '云同步'] },
  { id: 'pro_year', name: 'Pro 年付', price: 699, period: '/年', badge: '省 70%', color: 'text-brand-purple',
    savings: '原价 ¥2028/年,折后 ¥699', saveBadge: '推荐',
    features: ['Pro 月付全部', '优先客服', '自定义杀号策略', 'API 基础额度', '专属底池模板'] },
  { id: 'max', name: 'MAX 买断', price: 1999, period: '一次性', badge: '终身', color: 'text-brand-gold',
    savings: '约 12 个月订阅价格', saveBadge: '最划算',
    features: ['Pro 年付全部', '终身使用', '无月费', '无限制 API', '优先新功能', '专属客服群'] },
];

// 中文 name → 英文 id(给 PaymentModal 用)
const TIER_NAME_TO_ID = {
  'Pro 月付': 'pro_month',
  'Pro 年付': 'pro_year',
  'MAX 买断': 'max',
};

const TRIAL_HOURS = 24;

export default function ProfilePage() {
  const { user, logout, checkTrial, upgrade, setUser } = useUser();
  const location = useLocation();
  const [showLogin, setShowLogin] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [trial, setTrial] = useState({ onTrial: false, hoursLeft: 0, expired: false });
  const [tier, setTier] = useState(null);
  const [paymentTier, setPaymentTier] = useState(null);  // 支付档位(打开 PaymentModal)

  // 跨设备同步收藏
  const favs = useFavoritesStore(s => s.favorites);
  const lastSyncedAt = useFavoritesStore(s => s.lastSyncedAt);
  const syncing = useFavoritesStore(s => s.syncing);
  const removeFav = useFavoritesStore(s => s.remove);
  const removeById = useFavoritesStore(s => s.removeById);
  const clearAll = useFavoritesStore(s => s.clearAll);
  const fetchFromServer = useFavoritesStore(s => s.fetchFromServer);

  // 登录时拉服务器(强制从 server 覆盖本地缓存,避免手动补单后还显示试用)
  useEffect(() => {
    if (user?.id) {
      fetchFromServer();
      // 同步从 server 拿最新 user
      (async () => {
        try {
          const me = await api.me();
          if (me?.user) {
            setUser(me.user);
            setTier(me.user.tier);
            // 强制清掉本地 24h 试用残留(防 user_xxx 还是 Pro 月付)
            try {
              localStorage.removeItem(`user_${me.user.phone}`);
            } catch {}
          }
        } catch {}
      })();
      // 每 5s 轮询一次(确保手动补单后立即同步)
      const interval = setInterval(async () => {
        try {
          const me = await api.me();
          if (me?.user && (me.user.tier !== user.tier || me.user.tier_expires_at !== user.tier_expires_at)) {
            setUser(me.user);
            setTier(me.user.tier);
          }
        } catch {}
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      const t = checkTrial();
      setTrial(t);
      setTier(t.expired ? null : user.tier);

      // 登录/刷新后:检测 pending action(从 LoginModal 跳回来的支付请求)
      try {
        const pending = sessionStorage.getItem('fucai3d_pending_action');
        if (pending) {
          const action = JSON.parse(pending);
          if (action.type === 'upgrade' && action.tier) {
            sessionStorage.removeItem('fucai3d_pending_action');
            sessionStorage.removeItem('fucai3d_redirect_from');
            // 跳到支付 Modal
            setTimeout(() => setPaymentTier(action.tier), 100);
          }
        }
      } catch {}
    }
  }, [user]);

  const handleRemoveFav = (fav, i) => {
    if (fav.id) {
      removeById(fav.id);
    } else {
      removeFav(i);
    }
    toast.success('已删除');
  };

  const handleClearAll = async () => {
    if (confirm('确认清空所有收藏?(会同步删除云端数据)')) {
      await clearAll();
      toast.success('已清空');
    }
  };

  const handleLogout = () => {
    if (confirm('确认登出?24 小时试用仍会继续(但需要重新登录查看)')) {
      logout();
      toast.success('已登出');
    }
  };

  const handleUpgrade = async (tierNameOrId) => {
    // 兼容中英文:Pro 月付 → pro_month
    const tierId = TIER_NAME_TO_ID[tierNameOrId] || tierNameOrId;
    if (!user) {
      // 记录原意图(支付时用)
      sessionStorage.setItem('fucai3d_pending_action', JSON.stringify({ type: 'upgrade', tier: tierId }));
      sessionStorage.setItem('fucai3d_redirect_from', location.pathname);
      setShowLogin(true);
      return;
    }
    // 已登录 → 打开支付 Modal
    setPaymentTier(tierId);
  };

  // 支付成功回调
  const handlePaymentSuccess = async (r) => {
    setPaymentTier(null);
    toast.success('🎉 开通成功!', { duration: 4000 });
    // 刷新 /api/auth/me
    try {
      const me = await api.me();
      if (me?.user) {
        setUser(me.user);
        setTier(me.user.tier);
      }
    } catch {}
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 试用过期警示横幅(从 App 跳转过来时显示,且 user 还没付费) */}
      {location.state?.reason === 'trial_expired' && !user?.tier_expires_at && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 sm:p-5 border-2 border-accent-danger/50"
          style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(251,113,133,0.08) 100%)' }}
        >
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-accent-danger flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-accent-danger mb-1">
                🔒 试用已过期 · 全功能已锁定
              </div>
              <div className="text-xs text-ink-2 leading-relaxed">
                你的 24 小时 Pro 试用已结束。升级后立即解锁所有功能,包括大底池、智能选号、90 期回测、收藏跨设备同步等。
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="card">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <User size={22} className="text-brand-purple" />
          个人中心
        </h1>
        <p className="text-sm text-ink-3 mt-1">手机号登录 · 跨设备同步 · 会员管理</p>
      </div>

      {/* User card */}
      <div className="card">
        {user ? (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center text-xl font-bold flex-shrink-0">
              {user.name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-base font-semibold truncate">{user.name || `用户${(user.phone || '').slice(-4) || ''}`}</div>
                <TierBadge tier={user.tier} tierExpiresAt={user.tier_expires_at} />
              </div>
              <div className="text-xs text-ink-3 flex items-center gap-1 mt-0.5">
                <Phone size={10} />
                {user.phone ? user.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '未设置手机号'}
              </div>
              <div className="text-[10px] text-ink-3 mt-0.5">
                注册 {new Date(user.createdAt || user.created_at || Date.now()).toLocaleDateString('zh-CN')}
              </div>
              <Countdown tier={user.tier} tierExpiresAt={user.tier_expires_at} trialExpiresAt={user.trial_expires_at} />
            </div>
            <button
              onClick={handleLogout}
              className="btn-ghost text-xs flex items-center gap-1.5"
            >
              <LogOut size={12} /> 登出
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center flex-shrink-0">
              <User size={28} className="text-bg-base" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold">未登录</div>
              <div className="text-xs text-ink-3">手机号注册 · 24 小时自动试用 · 跨设备同步</div>
            </div>
            <button
              onClick={() => setShowLogin(true)}
              className="btn-primary flex items-center gap-2"
            >
              <LogIn size={14} /> 登录 / 注册
            </button>
          </div>
        )}
      </div>

      {/* 快速操作 5 宫格(高频入口) */}
      <div className="grid grid-cols-5 gap-2">
        <QuickAction
          icon={Wallet} label="钱包" subtitle="提现佣金"
          color="text-brand-gold" bg="bg-brand-gold/10"
          onClick={() => {
            const tab = document.querySelector('[data-tab="withdraw"]');
            if (tab) {
              tab.click();
              tab.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              toast('登录后查看提现功能');
            }
          }}
        />
        <QuickAction icon={Bell} label="智能提醒" subtitle="命中通知" color="text-brand-cyan" bg="bg-brand-cyan/10" to="/picks" />
        <QuickAction icon={ImageIcon} label="海报" subtitle="推荐分享" color="text-brand-rose" bg="bg-brand-rose/10" onClick={() => setShowPoster(true)} />
        <QuickAction icon={SettingsIcon} label="设置" subtitle="偏好/隐私" color="text-ink-2" bg="bg-white/5" to="/settings" />
        <QuickAction icon={HelpCircle} label="客服" subtitle="在线工单" color="text-brand-purple" bg="bg-brand-purple/10" to="/support" />
      </div>

      {/* Subscription tiers */}
      <div>
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Crown size={18} className="text-brand-gold" />
          会员方案
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TIERS.map(t => {
            const isCurrent = t.name === tier;
            return (
              <motion.div
                key={t.name}
                whileHover={{ y: -4 }}
                className={clsx(
                  'card cursor-pointer transition-all relative',
                  isCurrent ? 'border-brand-gold/50 ring-1 ring-brand-gold/30' : 'hover:border-brand-purple/40',
                  t.saveBadge === '推荐' && 'border-brand-purple/40',
                  t.saveBadge === '最划算' && 'border-brand-gold/40',
                )}
                onClick={() => handleUpgrade(t.name)}
              >
                {t.saveBadge && (
                  <div className={clsx(
                    'absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-bold',
                    t.saveBadge === '最划算' && 'bg-brand-gold text-bg-base',
                    t.saveBadge === '推荐' && 'bg-brand-purple text-white',
                  )}>
                    {t.saveBadge}
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div className={clsx('text-base font-bold', t.color)}>{t.name}</div>
                  <div className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
                    {t.badge}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-ink-3">¥</span>
                    <span className="text-2xl font-bold font-mono">{t.price}</span>
                    <span className="text-xs text-ink-3">{t.period}</span>
                  </div>
                  {t.savings && (
                    <div className="text-[10px] text-brand-cyan mt-1">{t.savings}</div>
                  )}
                </div>
                <ul className="space-y-1 text-xs text-ink-2">
                  {t.features.map(f => (
                    <li key={f} className="flex items-start gap-1.5">
                      <span className="text-brand-cyan flex-shrink-0">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent && (
                  <div className="mt-3 text-center text-xs text-brand-gold font-semibold py-1.5 rounded-lg bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center gap-1.5">
                    <Check size={12} /> 当前方案
                  </div>
                )}
                {!isCurrent && (
                  <button className="mt-3 w-full text-center text-xs font-semibold py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition">
                    立即开通
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
        <div className="mt-3 text-center text-xs text-ink-3">
          💡 首次访问自动获得 <span className="text-brand-gold font-semibold">24 小时 Pro 月付试用</span> · 试用结束后需选择方案继续使用
        </div>
      </div>

      {/* 推荐分享 · 三级分佣(放在会员方案正下方 — 让用户知道推广也能赚钱) */}
      {user && <ReferralCard />}

      {/* Favorites */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Star size={18} className="text-brand-rose" />
            收藏夹 ({favs.length})
            {user?.id ? (
              <span className="text-[10px] text-brand-cyan font-normal flex items-center gap-1 ml-1">
                <Cloud size={10} /> 跨设备同步
              </span>
            ) : (
              <span className="text-[10px] text-ink-3 font-normal flex items-center gap-1 ml-1">
                <CloudOff size={10} /> 仅本地
              </span>
            )}
          </h2>
          {user?.id && (
            <button
              onClick={() => fetchFromServer()}
              disabled={syncing}
              className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-50"
              title="从云端拉取"
            >
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
              {syncing ? '同步中' : '同步'}
            </button>
          )}
        </div>
        {user?.id && lastSyncedAt && (
          <div className="text-[10px] text-ink-3 mb-2 flex items-center gap-1">
            <Check size={9} className="text-brand-cyan" />
            上次同步:{new Date(lastSyncedAt).toLocaleTimeString('zh-CN')}
            <span className="ml-2 text-ink-3">登录即自动同步,新设备收藏无缝衔接</span>
          </div>
        )}
        {favs.length === 0 ? (
          <div className="text-center py-8 text-ink-3 text-sm">
            暂无收藏 · 去大底池或选号页面收藏底池
          </div>
        ) : (
          <div className="space-y-2">
            {favs.map((f, i) => (
              <div key={f.id || i} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {f.title}
                    {f._synced && <Cloud size={10} className="text-brand-cyan flex-shrink-0" />}
                    {f.id && <Cloud size={10} className="text-brand-cyan flex-shrink-0" />}
                  </div>
                  <div className="text-xs text-ink-3 mt-0.5">
                    {new Date(f.time).toLocaleString('zh-CN')} · {f.count} 注
                    {f.level && <span className="ml-2 text-brand-gold">[{f.level}]</span>}
                  </div>
                  <div className="text-[10px] text-ink-2 font-mono mt-1 truncate">
                    {f.codes}
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(f.codes);
                    toast.success('已复制');
                  }}
                  className="btn-ghost p-2"
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={() => handleRemoveFav(f, i)}
                  className="btn-ghost p-2 hover:border-accent-danger/40"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {favs.length > 0 && (
          <div className="mt-3 text-center">
            <button
              onClick={handleClearAll}
              className="btn-ghost text-xs text-ink-3 hover:text-accent-danger"
            >
              <Trash2 size={11} className="inline mr-1" /> 清空所有
            </button>
          </div>
        )}
      </div>

      {/* 客服中心入口 */}
      <Link
        to="/support"
        className="card hover:border-brand-cyan/40 transition flex items-center gap-3 group"
        style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.08) 0%, rgba(168,85,247,0.05) 100%)' }}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center flex-shrink-0">
          <HelpCircle size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold">客服中心</div>
          <div className="text-xs text-ink-2 mt-0.5">常见问题 · 提交工单 · 联系我们</div>
        </div>
        <ChevronRight size={18} className="text-brand-cyan group-hover:translate-x-1 transition" />
      </Link>

      {/* 用户增长中心入口 */}
      <Link
        to="/social"
        className="card hover:border-brand-gold/40 transition flex items-center gap-3 group relative"
        style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(34,211,238,0.05) 100%)' }}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-gold to-brand-cyan flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold flex items-center gap-2">
            用户增长中心
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold font-semibold">NEW</span>
          </div>
          <div className="text-xs text-ink-2 mt-0.5">跟单大 V · 实战记录 · 勋章 · 推送</div>
        </div>
        <ChevronRight size={18} className="text-brand-gold group-hover:translate-x-1 transition" />
      </Link>

      {/* 设置入口 */}
      <Link
        to="/settings"
        className="card hover:border-white/30 transition flex items-center gap-3 group"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-ink-2 to-ink-3 flex items-center justify-center flex-shrink-0">
          <SettingsIcon size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold">设置</div>
          <div className="text-xs text-ink-2 mt-0.5">个人资料 · 隐私 · 字体 · 注销</div>
        </div>
        <ChevronRight size={18} className="text-ink-3 group-hover:translate-x-1 transition" />
      </Link>

      {/* 账户安全 — 改密码 */}
      <ChangePasswordCard user={user} />

      <LoginModal
        open={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => setShowLogin(false)}
      />

      <PosterModal
        open={showPoster}
        onClose={() => setShowPoster(false)}
      />

      {/* 支付弹窗 */}
      {paymentTier && (
        <PaymentModal
          tier={paymentTier}
          onClose={() => setPaymentTier(null)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}

// 会员等级徽章
function TierBadge({ tier, tierExpiresAt }) {
  // 永久(MAX 买断 / Pro 永久)
  if (tierExpiresAt === null || (tier && (tier.includes('买断') || tier === 'MAX 买断' || tier === 'Pro 永久'))) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-400/40">
        <Crown size={11} className="text-amber-300" />
        <span className="text-[10px] font-bold text-amber-200">{tier || '永久'}</span>
      </div>
    );
  }
  // 已付费(Pro 月付 / Pro 年付)
  if (tierExpiresAt && tierExpiresAt > Date.now()) {
    const isYear = tier?.includes('年');
    return (
      <div className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md border',
        isYear
          ? 'bg-cyan-500/15 border-cyan-400/40'
          : 'bg-purple-500/15 border-purple-400/40'
      )}>
        <Star size={11} className={isYear ? 'text-cyan-300' : 'text-purple-300'} />
        <span className={clsx('text-[10px] font-bold', isYear ? 'text-cyan-200' : 'text-purple-200')}>
          {tier}
        </span>
      </div>
    );
  }
  // 已过期
  if (tierExpiresAt && tierExpiresAt <= Date.now() && tier && tier !== '试用') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-400/40">
        <Clock size={11} className="text-red-300" />
        <span className="text-[10px] font-bold text-red-200">{tier}已过期</span>
      </div>
    );
  }
  // 试用中
  if (tier === '试用' || tier === 'Pro 月付') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-400/40">
        <Sparkles size={11} className="text-blue-300" />
        <span className="text-[10px] font-bold text-blue-200">试用中</span>
      </div>
    );
  }
  // 无会员
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
      <User size={11} className="text-ink-3" />
      <span className="text-[10px] text-ink-3">无会员</span>
    </div>
  );
}

// 倒计时组件(每秒刷新)
function Countdown({ tier, tierExpiresAt, trialExpiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // 永久
  if (tierExpiresAt === null || (tier && (tier.includes('买断') || tier === 'MAX 买断' || tier === 'Pro 永久'))) {
    return (
      <div className="text-[10px] text-amber-300 mt-1 flex items-center gap-1 font-semibold">
        <Crown size={9} /> 永久会员 · 终身使用
      </div>
    );
  }

  // 已付费剩余
  if (tierExpiresAt && tierExpiresAt > now) {
    const ms = tierExpiresAt - now;
    return (
      <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1 font-mono">
        <Clock size={9} />
        {tier} · 还剩 <span className="font-bold">{formatRemaining(ms)}</span>
      </div>
    );
  }

  // 已过期
  if (tierExpiresAt && tierExpiresAt <= now && tier && tier !== '试用') {
    return (
      <div className="text-[10px] text-red-400 mt-1 font-semibold">
        ⚠ {tier}已过期 · 请续费
      </div>
    );
  }

  // 试用中(trial_expires_at 还有)
  if (trialExpiresAt && trialExpiresAt > now) {
    const ms = trialExpiresAt - now;
    return (
      <div className="text-[10px] text-blue-300 mt-1 flex items-center gap-1 font-mono">
        <Sparkles size={9} />
        24 小时试用 · 还剩 <span className="font-bold">{formatRemaining(ms)}</span>
      </div>
    );
  }

  // 试用过期
  if (trialExpiresAt && trialExpiresAt <= now) {
    return (
      <div className="text-[10px] text-accent-danger mt-1 font-semibold">
        ⚠ 试用已过期 · 请选择方案
      </div>
    );
  }

  // 无会员
  return (
    <div className="text-[10px] text-ink-3 mt-1">
      未开通会员
    </div>
  );
}

function formatRemaining(ms) {
  if (ms <= 0) return '已过期';
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const secs = sec % 60;
  if (days > 0) return `${days}天 ${hours}小时 ${mins}分`;
  if (hours > 0) return `${hours}小时 ${mins}分 ${secs}秒`;
  if (mins > 0) return `${mins}分 ${secs}秒`;
  return `${secs}秒`;
}

// 快速操作 4 宫格单元
function QuickAction({ icon: Icon, label, subtitle, color, bg, to, onClick }) {
  const content = (
    <div className="flex flex-col items-center justify-center p-3 rounded-xl border border-white/5 hover:border-white/20 transition cursor-pointer">
      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center mb-1.5 border border-white/5', bg)}>
        <Icon size={18} className={color} />
      </div>
      <div className="text-xs font-semibold">{label}</div>
      <div className="text-[9px] text-ink-3 mt-0.5">{subtitle}</div>
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  return <div onClick={onClick}>{content}</div>;
}
